import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PendingButton } from '@/components/ui/pending-button';
import {
  useFilingSendStatus,
  useSendFilingToState,
  useConfirmFilingDestination,
  useDownloadSignedFiling,
  FilingSendActionError,
} from '@/hooks/queries/useFilingSend';
import { useDownloadReportPackage } from '@/hooks/queries/useFinanceReports';
import { SignaturePad } from './SignaturePad';
import { triggerBrowserDownload } from './downloadHelpers';
import { answered, reportsUnreadyMessage } from '@/lib/reportsRead';

/**
 * Issue #861 — the treasurer "authorize and send" surface. It displays the resolved filing
 * destination verbatim on the near side of the send, captures the treasurer's typed authorization and
 * drawn signature, gates a county destination on a one-time confirmation, and surfaces delivery /
 * bounce / failure — always alongside the download-and-file-it-yourself escape hatch. Jurisdiction-
 * neutral: it renders the server-computed lens (channel, destination, confirmation need), never a
 * state code.
 */
export interface AuthorizeSendModalProps {
  reportId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// The pad emits a full data URL ("data:image/png;base64,XXXX"); the API wants the base64 payload only.
function base64Payload(dataUrl: string | null): string | null {
  if (!dataUrl) return null;
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

export function AuthorizeSendModal({ reportId, open, onOpenChange }: AuthorizeSendModalProps) {
  const statusRead = useFilingSendStatus(open ? reportId : null);
  const status = answered(statusRead);
  const isLoading = statusRead.status === 'loading';
  // The send is gated on what this read says (destination, confirmation, already sent).
  const statusUnready = reportsUnreadyMessage('send status', statusRead);
  const { sendToState } = useSendFilingToState();
  const { confirmDestination } = useConfirmFilingDestination();
  const { downloadPackage } = useDownloadReportPackage();
  const { downloadSignedFiling } = useDownloadSignedFiling();

  const [typedName, setTypedName] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'send' | 'download' | 'confirm'>(null);
  const [error, setError] = useState<string | null>(null);

  const destination = status?.resolvedDestination ?? null;
  const isEmailChannel = status?.channel === 'Email';
  const needsConfirmation = !!status?.requiresDestinationConfirmation && !status?.destinationConfirmed;
  const alreadySent = !!status?.hasBeenSent;

  const canSend =
    isEmailChannel &&
    !!destination &&
    typedName.trim().length > 0 &&
    !needsConfirmation &&
    !statusUnready &&
    busy === null;

  const handleConfirmDestination = async () => {
    if (!destination) return;
    setBusy('confirm');
    setError(null);
    try {
      await confirmDestination({ reportId, destination });
    } catch (e) {
      setError(e instanceof FilingSendActionError ? e.message : 'Could not confirm the destination.');
    } finally {
      setBusy(null);
    }
  };

  const handleSend = async () => {
    setBusy('send');
    setError(null);
    try {
      await sendToState({
        reportId,
        authorizedByTypedName: typedName.trim(),
        signatureImageBase64: base64Payload(signatureDataUrl),
      });
    } catch (e) {
      // A county destination not yet confirmed comes back as a coded conflict — surface it so the
      // treasurer confirms the address first, rather than reading a generic failure.
      setError(e instanceof FilingSendActionError ? e.message : 'The filing could not be sent.');
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    setBusy('download');
    setError(null);
    try {
      const payload = base64Payload(signatureDataUrl);
      if (payload) {
        const { fileBlob, fileName } = await downloadSignedFiling({ reportId, signatureImageBase64: payload });
        triggerBrowserDownload(fileBlob, fileName);
      } else {
        const { zipBlob, fileName } = await downloadPackage(reportId);
        triggerBrowserDownload(zipBlob, fileName);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The filing could not be downloaded.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0" data-testid="authorize-send-modal">
        <DialogHeader>
          <DialogTitle>Authorize and send this filing</DialogTitle>
          <DialogDescription>
            The report is sent to the receiving office on your behalf. You authorize each send — nothing
            goes out silently.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {statusUnready && (
            <p role="alert" className="text-sm text-destructive" data-testid="authorize-send-status-unready">
              {statusUnready}
            </p>
          )}

          {/* The resolved destination, verbatim, on the near side of the send. */}
          <section className="rounded-md border bg-muted/30 p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Filing goes to</div>
            {destination ? (
              <div className="mt-1 font-mono text-sm" data-testid="send-destination">
                {destination}
              </div>
            ) : (
              <div className="mt-1 text-sm text-muted-foreground" data-testid="send-destination-missing">
                No filing destination is set yet. Add your receiving office's address in finance settings,
                or download the filing and send it yourself.
              </div>
            )}
            {destination && (
              <p className="mt-2 text-xs text-muted-foreground">
                Delivery to this inbox is <strong>not</strong> the same as the office accepting your filing —
                the receiving office's acceptance happens out of band.
              </p>
            )}
          </section>

          {/* County-destination confirmation: recorded once, never inferred from settings. */}
          {needsConfirmation && destination && (
            <section className="rounded-md border border-amber-400 bg-amber-50 p-3 dark:bg-amber-950/30">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  data-testid="county-confirm-checkbox"
                  // Controlled + always-unchecked while this block shows: the block only renders
                  // when the destination is NOT yet confirmed, so a SUCCESSFUL confirm unmounts it
                  // (destinationConfirmed flips true on refetch). A FAILED confirm leaves the box
                  // unchecked and re-clickable, rather than stuck visually-checked but unrecorded.
                  checked={false}
                  disabled={busy !== null}
                  onChange={() => void handleConfirmDestination()}
                />
                <span>
                  I confirm <span className="font-mono">{destination}</span> is my receiving office's
                  correct address. There is no state fallback for a county filing — a wrong-but-deliverable
                  address is never bounced.
                </span>
              </label>
            </section>
          )}

          {/* Typed authorization + drawn signature — one deliberate action. With no destination there
              is nothing to authorize; the signature still signs the downloaded cover. */}
          <section className="space-y-3">
            {destination && (
            <div className="space-y-1">
              <label htmlFor="authorize-typed-name" className="text-sm font-medium">
                Your name (authorizes this send)
              </label>
              <input
                id="authorize-typed-name"
                data-testid="authorize-typed-name"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={typedName}
                disabled={busy !== null}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Type your full name"
              />
            </div>
            )}
            <div className="space-y-1">
              <div className="text-sm font-medium">Your signature{destination ? '' : ' (optional)'}</div>
              <p className="text-xs text-muted-foreground">
                {destination
                  ? "Draw your signature — it is stamped on the report's signature line and filed with it."
                  : "Draw your signature to stamp it on the downloaded report's signature line."}
              </p>
              <SignaturePad onChange={setSignatureDataUrl} disabled={busy !== null} data-testid="signature-pad" />
            </div>
          </section>

          {/* Delivery / bounce / failure. */}
          {alreadySent && (
            <section className="rounded-md border p-3 text-sm" data-testid="send-delivery-status">
              <div>
                Last send: <strong>{status?.status}</strong>
                {status?.sentAt && <> on {new Date(status.sentAt).toLocaleDateString()}</>}
              </div>
              {status?.status === 'Delivered' && (
                <p className="mt-1 text-muted-foreground" data-testid="delivered-not-accepted-note">
                  Delivered to the receiving office's inbox. That is not the same as the office accepting
                  your filing — watch for their confirmation, which arrives out of band.
                </p>
              )}
              {status?.requiresSelfSendFallback && (
                <div
                  className="mt-2 rounded-md border border-destructive bg-destructive/10 p-2 text-destructive"
                  data-testid="send-fallback-note"
                >
                  The send {status?.status === 'Bounced' ? 'bounced' : 'failed'}
                  {status?.bounceReason ? ` (${status.bounceReason})` : ''}. Download the filing below and
                  send it yourself — a bounce never blocks your filing.
                </div>
              )}
            </section>
          )}

          {!isEmailChannel && !isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="download-only-note">
              This filing is sent by download — save it below and file it with your receiving office
              yourself.
            </p>
          )}

          {error && (
            <p className="text-sm text-destructive" data-testid="authorize-send-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-row">
          {/* The download escape hatch is always available — never only on failure. */}
          <Button
            type="button"
            variant="outline"
            onClick={handleDownload}
            disabled={busy !== null}
            data-testid="download-self-send-button"
          >
            Download to file it yourself
          </Button>
          {isEmailChannel && destination && (
            <PendingButton
              type="button"
              pending={busy === 'send'}
              onClick={handleSend}
              disabled={!canSend}
              data-testid="authorize-send-button"
            >
              Authorize and send
            </PendingButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
